import { z } from 'zod';
import { Dateish, intList } from './common';
import { AttendantStatusSchema } from './presence';
import { ReportPeriodQuerySchema } from './reports';

/**
 * Atendimento (spec 071): o que o supervisor vê das conversas — "Agora" (o
 * monitor) e "Período" (o relatório, com a lista que é o histórico). As
 * definições de cada número estão na spec e na tela, num (i); aqui ficam os
 * limites que as duas pontas usam.
 */

/** espera até 5 min é normal; de 5 a 15 min, atenção; acima, alerta (spec 071, D7) */
export const ATTENDANCE_WAIT_WARN_SECONDS = 5 * 60;
export const ATTENDANCE_WAIT_ALERT_SECONDS = 15 * 60;
/** teto do período: três meses, o mesmo horizonte do relatório da Blip */
export const ATTENDANCE_PERIOD_MAX_DAYS = 92;
/** a lista de conversas do período vem inteira até aqui; acima, `chatsTruncated` */
export const ATTENDANCE_CHATS_MAX = 2000;
/** o monitor se lê de novo sozinho neste intervalo (spec 071, D6) */
export const ATTENDANCE_NOW_REFRESH_MS = 15_000;

export type AttendanceWaitLevel = 'ok' | 'warn' | 'alert';

/** a cor de uma espera, a mesma regra no monitor e no relatório */
export function attendanceWaitLevel(seconds: number | null): AttendanceWaitLevel {
  if (seconds === null || seconds < ATTENDANCE_WAIT_WARN_SECONDS) return 'ok';
  return seconds < ATTENDANCE_WAIT_ALERT_SECONDS ? 'warn' : 'alert';
}

// ── Agora ───────────────────────────────────────────────────────────────────

export const AttendanceNowQuerySchema = z.object({
  desk_ids: intList.optional(),
});
export type AttendanceNowQuery = z.infer<typeof AttendanceNowQuerySchema>;

/** uma conversa aberta: na fila, ou com alguém */
export const AttendanceNowChatSchema = z.object({
  chatId: z.number().int(),
  requestorName: z.string(),
  clientName: z.string().nullable(),
  /** a mesa efetiva: a do ticket ligado, senão a da conversa (spec 068) */
  deskId: z.number().int().nullable(),
  deskName: z.string().nullable(),
  origin: z.enum(['widget', 'whatsapp', 'manual']),
  /** entrada na fila (a transferência do bot, a 1ª mensagem do cliente ou a criação pela equipe); null: não chegou à equipe */
  enteredAt: Dateish.nullable(),
  responsibleId: z.number().int().nullable(),
  responsibleName: z.string().nullable(),
  /** desde quando está com o responsável atual (null na fila) */
  assumedAt: Dateish.nullable(),
  /** a mensagem do cliente mais antiga ainda sem resposta pública (chat-awaiting) */
  awaitingSince: Dateish.nullable(),
});
export type AttendanceNowChat = z.infer<typeof AttendanceNowChatSchema>;

/** uma pessoa da equipe: o status de agora e as conversas na mão */
export const AttendanceNowAgentSchema = z.object({
  userId: z.number().int(),
  userName: z.string(),
  status: AttendantStatusSchema,
  reasonName: z.string().nullable(),
  /** desde quando está no status de agora (null em `away` sem registro) */
  since: Dateish.nullable(),
  /** conversas abertas com ela, no recorte de mesas de quem olha */
  chats: z.number().int(),
  /** das conversas dela, o cliente que espera resposta há mais tempo */
  oldestAwaitingSince: Dateish.nullable(),
});
export type AttendanceNowAgent = z.infer<typeof AttendanceNowAgentSchema>;

/** uma medida de tempo: média, máximo e em quantas conversas (ou respostas) */
export const AttendanceMetricSchema = z.object({
  count: z.number().int(),
  avgSeconds: z.number().int().nullable(),
  maxSeconds: z.number().int().nullable(),
});
export type AttendanceMetric = z.infer<typeof AttendanceMetricSchema>;

export const AttendanceTotalsSchema = z.object({
  /** entraram na fila no período, mais as iniciadas pela equipe */
  opened: z.number().int(),
  /** das abertas, as iniciadas pela equipe ("Nova conversa") */
  openedByTeam: z.number().int(),
  closed: z.number().int(),
  /** das encerradas, as que ninguém da equipe assumiu */
  closedWithoutAttendance: z.number().int(),
  wait: AttendanceMetricSchema,
  firstResponse: AttendanceMetricSchema,
  response: AttendanceMetricSchema,
  service: AttendanceMetricSchema,
});
export type AttendanceTotals = z.infer<typeof AttendanceTotalsSchema>;

export const AttendanceDeskOptionSchema = z.object({ id: z.number().int(), name: z.string() });
export type AttendanceDeskOption = z.infer<typeof AttendanceDeskOptionSchema>;

export const AttendanceNowSchema = z.object({
  generatedAt: Dateish,
  /** na fila, a que espera há mais tempo primeiro */
  queue: z.array(AttendanceNowChatSchema),
  /** com alguém, a que está sem resposta há mais tempo primeiro */
  inService: z.array(AttendanceNowChatSchema),
  /** abertas e ainda com o bot externo: ninguém da equipe deve nada a elas */
  withBot: z.number().int(),
  agents: z.array(AttendanceNowAgentSchema),
  /** o dia de hoje (São Paulo), com as definições do período */
  today: AttendanceTotalsSchema,
  /** as mesas que quem olha pode filtrar */
  desks: z.array(AttendanceDeskOptionSchema),
});
export type AttendanceNow = z.infer<typeof AttendanceNowSchema>;

// ── Período ─────────────────────────────────────────────────────────────────

export const AttendancePeriodQuerySchema = ReportPeriodQuerySchema.extend({
  desk_ids: intList.optional(),
  user_ids: intList.optional(),
});
export type AttendancePeriodQuery = z.infer<typeof AttendancePeriodQuerySchema>;

export const AttendanceByDeskSchema = AttendanceTotalsSchema.extend({
  deskId: z.number().int().nullable(),
  deskName: z.string(),
});
export type AttendanceByDesk = z.infer<typeof AttendanceByDeskSchema>;

export const AttendanceByAgentSchema = z.object({
  userId: z.number().int(),
  userName: z.string(),
  /** conversas do período que a pessoa teve na mão em algum momento */
  attended: z.number().int(),
  /** conversas encerradas por ela no período */
  closed: z.number().int(),
  /** das conversas em que ela deu a 1ª resposta */
  firstResponse: AttendanceMetricSchema,
  /** das respostas dela no período */
  response: AttendanceMetricSchema,
  /** das conversas que ela encerrou no período */
  service: AttendanceMetricSchema,
});
export type AttendanceByAgent = z.infer<typeof AttendanceByAgentSchema>;

export const AttendanceDaySchema = z.object({
  /** YYYY-MM-DD, São Paulo */
  day: z.string(),
  opened: z.number().int(),
  closed: z.number().int(),
  closedWithoutAttendance: z.number().int(),
});
export type AttendanceDay = z.infer<typeof AttendanceDaySchema>;

export const AttendanceReasonSchema = z.object({
  reasonId: z.number().int().nullable(),
  reasonName: z.string(),
  count: z.number().int(),
});
export type AttendanceReason = z.infer<typeof AttendanceReasonSchema>;

/** uma conversa do período: a linha do histórico */
export const AttendancePeriodChatSchema = z.object({
  chatId: z.number().int(),
  requestorName: z.string(),
  clientName: z.string().nullable(),
  deskId: z.number().int().nullable(),
  deskName: z.string().nullable(),
  origin: z.enum(['widget', 'whatsapp', 'manual']),
  enteredAt: Dateish,
  closedAt: Dateish.nullable(),
  closedByName: z.string().nullable(),
  closeReasonName: z.string().nullable(),
  canceled: z.boolean(),
  /** quem teve a conversa na mão, na ordem em que assumiram */
  attendants: z.array(z.string()),
  waitSeconds: z.number().int().nullable(),
  firstResponseSeconds: z.number().int().nullable(),
  serviceSeconds: z.number().int().nullable(),
});
export type AttendancePeriodChat = z.infer<typeof AttendancePeriodChatSchema>;

export const AttendancePeriodSchema = z.object({
  totals: AttendanceTotalsSchema,
  byDesk: z.array(AttendanceByDeskSchema),
  byAgent: z.array(AttendanceByAgentSchema),
  days: z.array(AttendanceDaySchema),
  reasons: z.array(AttendanceReasonSchema),
  /** a mais recente primeiro */
  chats: z.array(AttendancePeriodChatSchema),
  chatsTruncated: z.boolean(),
  desks: z.array(AttendanceDeskOptionSchema),
});
export type AttendancePeriod = z.infer<typeof AttendancePeriodSchema>;
